<!-- app/javascript/dashboard/routes/dashboard/settings/emailTemplates/builder/canvas/elements/CanvasProduct.vue -->
<script setup>
// Produto no canvas: mesma disposição do e-mail (1 ao lado ou em cima; 2–3 em
// cartões), preço em reais e botão com as cores do bloco.
import { computed, inject } from 'vue';
import { buttonColors, textColor, onDark } from '../canvasStyles';
import { formatBRL } from '../../document/product';

const props = defineProps({
  element: { type: Object, required: true },
  bgValue: { type: String, default: null },
});
defineOptions({ inheritAttrs: false });
const ed = inject('emailEditor');
const theme = computed(() => ed.template.value?.theme || {});
const st = computed(() => props.element.style || {});
const items = computed(() => props.element.items || []);
const mode = computed(() => {
  if (items.value.length > 1) return 'grid';
  return props.element.layout === 'single_side' ? 'side' : 'top';
});
const colors = computed(() =>
  buttonColors(
    { bg: st.value.button_bg, color: st.value.button_color },
    theme.value
  )
);
const fg = computed(() => textColor(null, props.bgValue, theme.value));
const muted = computed(() => textColor('muted', props.bgValue, theme.value));
// Mesma regra do e-mail: preço na cor do texto sobre fundo escuro, senão primária.
const priceColor = computed(() =>
  onDark(props.bgValue) ? fg.value : theme.value.primary
);
const GRID_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' };
const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };
const showOld = item =>
  item.old_price != null && item.price != null && item.old_price > item.price;
const hasLink = url => !!url && url !== 'https://';
</script>

<template>
  <div
    data-product
    :data-mode="mode"
    :class="
      mode === 'grid'
        ? ['grid gap-4', GRID_COLS[items.length] || 'grid-cols-3']
        : ''
    "
  >
    <div
      v-for="(item, i) in items"
      :key="i"
      data-product-card
      :class="[
        st.card_border ? 'rounded-[10px] border border-[#e6e9ee] p-3' : '',
        mode === 'side' ? 'flex gap-4' : 'flex flex-col',
      ]"
    >
      <div :class="mode === 'side' ? 'w-2/5 shrink-0' : 'w-full'">
        <img
          v-if="item.media?.url"
          :src="item.media.url"
          :alt="item.name"
          class="block h-auto w-full rounded-lg"
          draggable="false"
        />
        <div
          v-else
          class="flex h-24 w-full items-center justify-center rounded-lg border-2 border-dashed border-n-weak bg-n-solid-2 text-n-slate-10"
        >
          <span class="i-lucide-image-plus size-5" />
        </div>
      </div>
      <div class="min-w-0 flex-1 pt-3" :class="ALIGN[st.align] || 'text-left'">
        <p class="text-[17px] font-bold leading-snug" :style="{ color: fg }">
          {{ item.name }}
        </p>
        <p
          v-if="item.description"
          class="pt-1.5 text-sm leading-normal"
          :style="{ color: muted }"
        >
          {{ item.description }}
        </p>
        <p v-if="item.price != null" class="pt-2">
          <span
            v-if="showOld(item)"
            class="mr-1 text-[13px] line-through"
            :style="{ color: muted }"
            >{{ formatBRL(item.old_price) }}</span
          >
          <span class="text-xl font-bold" :style="{ color: priceColor }">{{
            formatBRL(item.price)
          }}</span>
        </p>
        <span
          v-if="hasLink(item.url) && item.button_label"
          class="mt-3 inline-block rounded-lg px-5 py-3 text-sm font-bold"
          :style="{ backgroundColor: colors.bg, color: colors.fg }"
          >{{ item.button_label }}</span
        >
      </div>
    </div>
  </div>
</template>
